import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import SiChevronRight from '@/icons/SiChevronRight';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { PageLoading } from '@/layouts/PageLoading';
import { Container } from '@/layouts/main/Main';
import { getMyCatalogueLink } from '@/lib/getMyLinks';
import { cn } from '@/lib/utils';
import { useRole } from '@/services/authentication/hooks/user';
import { PageTitle } from '@/utils/Title';
import { FunctionComponent, Suspense, useState } from 'react';

interface StockModelCardProps {
  title: string;
  points: string[];
  selected: boolean;
  onClickCallback: () => void;
  className?: string;
}

const StockModelCard: FunctionComponent<StockModelCardProps> = ({
  selected,
  title,
  points,
  onClickCallback,
  className
}) => {
  const selectedMarker = (
    <div
      onClick={onClickCallback}
      className={cn(
        'w-7 cursor-pointer h-7 rounded-full border flex items-center justify-center',
        selected ? 'bg-primary-700' : ''
      )}
    >
      {selected && <div className="w-2 h-2 rounded-full border bg-white"></div>}
    </div>
  );

  return (
    <div className="flex flex-1 items-center flex-col space-y-2">
      {selectedMarker}
      <div
        onClick={onClickCallback}
        className={cn(
          'cursor-pointer w-full space-y-3 flex flex-col items-center border rounded-md py-4 px-6',
          className
        )}
      >
        <p className="text-2xl font-bold">{title}</p>
        <ul className="space-y-2 text-center">
          {points.map((point, index) => (
            <li className="font-medium" key={`${title}-${index}`}>
              {point}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

enum StockModel {
  basicBetterBest,
  ownStock
}

const SelectStockModelPageContent: FunctionComponent = () => {
  const role = useRole();
  const link = getMyCatalogueLink(role);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Catalogue',
      to: link
    },
    {
      name: 'Create',
      to: ''
    }
  ];

  const [selectedModel, setSelectedModel] = useState<StockModel>();

  const onGetStarted = () => {
    // Request to set model?
    // Navigate
  };

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Create</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container className="w-full py-8 space-y-6">
          <p className="text-xl font-bold">Please select a stock model.</p>

          <div className="flex justify-between space-x-20">
            <StockModelCard
              title="Basic - Better - Best"
              points={[
                'This is stock that is available via DaaS portal.',
                'Simple, quick and easy without having to hold your own stock.',
                'We own the stock and you take it when you need it.'
              ]}
              selected={selectedModel === StockModel.basicBetterBest}
              onClickCallback={() => setSelectedModel(StockModel.basicBetterBest)}
              className="bg-secondary-400 border-secondary-700"
            />

            <StockModelCard
              title="Choose your own stock levels"
              points={[
                'Gives you the full catalogue',
                'Allows you to set individual stock levels.',
                'Fully customisable. You own your own stock.'
              ]}
              selected={selectedModel === StockModel.ownStock}
              onClickCallback={() => setSelectedModel(StockModel.ownStock)}
              className="bg-primary-400 border-primary-700"
            />
          </div>

          <Button onClick={onGetStarted} className="ml-auto" variant={ButtonVariant.Primary}>
            <span>Get Started</span> <SiChevronRight className="text-3xl" />
          </Button>
        </Container>
      </div>
    </>
  );
};

const SelectStockModelPage: FunctionComponent = () => {
  return (
    <>
      <PageTitle>MSP Settings</PageTitle>
      <Suspense fallback={<PageLoading />}>
        <SelectStockModelPageContent />
      </Suspense>
    </>
  );
};

export default SelectStockModelPage;
